import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { View, Text, Card } from "../base";
import { Icon } from "../base/Icon";
import { ComparisonDataPoint, COLORS } from "../../utils/chartData";

interface StringOutcomeComparisonChartProps {
  data: ComparisonDataPoint[];
  title?: string;
  significanceData?: Record<string, { significance: string; confidence: number; pValue: number }>;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <View className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
        <Text className="font-medium mb-2">{label}</Text>
        {payload.map((entry: any, index: number) => (
          <View
            key={index}
            className="flex items-center space-x-2"
          >
            <View
              className="w-3 h-3 rounded"
              style={{ backgroundColor: entry.color }}
            />
            <Text className="text-sm">
              {entry.name}: {entry.value}%
            </Text>
          </View>
        ))}
      </View>
    );
  }
  return null;
};

export const StringOutcomeComparisonChart: React.FC<StringOutcomeComparisonChartProps> = ({
  data,
  title = "String Outcome Comparison",
  significanceData = {},
}) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <View className="p-6">
          <View className="flex items-center mb-4">
            <Icon
              name="type"
              size={20}
              className="mr-2 text-purple-600 dark:text-purple-400"
            />
            <Text className="text-lg font-semibold">{title}</Text>
          </View>
          <View className="flex justify-center items-center h-64">
            <Text className="text-gray-500 dark:text-gray-400">No string outcome data available</Text>
          </View>
        </View>
      </Card>
    );
  }

  // Group data by variant and string value
  const groupedData = data.reduce(
    (acc, item) => {
      const variant = item.variant as string;
      const stringValue = item.stringValue as string;
      const rateValue = item.rateValue as number;

      if (!acc[variant]) {
        acc[variant] = { variant };
      }
      acc[variant][stringValue] = rateValue;
      return acc;
    },
    {} as Record<string, any>,
  );

  const chartData = Object.values(groupedData);
  const stringValues = Array.from(new Set(data.map((item) => item.stringValue as string)));

  return (
    <Card>
      <View className="p-6">
        <View className="flex items-center mb-4">
          <Icon
            name="type"
            size={20}
            className="mr-2 text-purple-600 dark:text-purple-400"
          />
          <Text className="text-lg font-semibold">{title}</Text>
        </View>

        <ResponsiveContainer
          width="100%"
          height={400}
        >
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="variant" />
            <YAxis label={{ value: "Rate (%)", angle: -90, position: "insideLeft" }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {stringValues.map((stringValue, index) => (
              <Bar
                key={`bar-${stringValue}-${index}`}
                dataKey={stringValue}
                fill={COLORS[index % COLORS.length]}
                radius={[4, 4, 0, 0]}
                name={stringValue}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>

        {/* Statistical Significance Indicators */}
        {Object.keys(significanceData).length > 0 && (
          <View className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Text className="text-sm font-medium mb-2">Statistical Significance:</Text>
            <View className="flex flex-wrap gap-2">
              {Object.entries(significanceData).map(([variant, significanceResult]) => (
                <View
                  key={variant}
                  className="flex items-center space-x-1"
                >
                  <Text className="text-xs font-medium">{variant}:</Text>
                  <View
                    className={`px-2 py-1 rounded text-xs ${
                      significanceResult.confidence >= 99
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : significanceResult.confidence >= 95
                          ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                          : significanceResult.confidence >= 90
                            ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                            : significanceResult.confidence >= 80
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                              : significanceResult.confidence >= 70
                                ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                    }`}
                  >
                    <Text className="text-xs">{significanceResult.significance}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Summary Table */}
        <View className="mt-6">
          <Text className="text-sm font-medium mb-3">Detailed Breakdown:</Text>
          <View className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Variant</th>
                  {stringValues.map((value, index) => (
                    <th
                      key={`header-${value}-${index}`}
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      {value}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {Object.entries(groupedData).map(([variant, data]) => (
                  <tr key={variant}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{variant}</td>
                    {stringValues.map((value, index) => (
                      <td
                        key={`cell-${value}-${index}`}
                        className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                      >
                        {data[value] ? `${data[value].toFixed(1)}%` : "0.0%"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </View>
        </View>
      </View>
    </Card>
  );
};
